import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, authApi, setAuthToken } from "../lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Only attempt /auth/me if we have a JWT or are returning from an OAuth callback.
    // Otherwise we'd needlessly hit /auth/me as anonymous and trigger CORS preflight on every page load.
    const hasToken = !!localStorage.getItem("mc_token");
    const hasOauthHash = typeof window !== "undefined" && window.location.hash?.includes("session_id=");
    if (!hasToken && !hasOauthHash) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      // Use authApi (withCredentials) so the OAuth httpOnly cookie is sent if present.
      const res = await authApi.get("/auth/me");
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: skip /me check if returning from OAuth callback
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const loginWithPassword = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    setAuthToken(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async (email, password, name) => {
    const res = await api.post("/auth/register", { email, password, name });
    setAuthToken(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setAuthToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, loginWithPassword, register, logout, refresh: checkAuth, setUser }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
